
import React from 'react';
import { FormattedHTMLMessage, injectIntl } from 'react-intl';
import {
    VerificationService,
    MedicalProfessionalPersonalInfoViewModel,
    FieldValidationErrors,
    ExtendedFieldValidationErrors,
    MedicalProfessionalPersonalInfoResponse,
    hasFailedInstantMatch,
    getAvailableCountryChoices,
    FormSelectChoice,
    MedicalProfessionalStatusDefaultMessagesEnum,
    InputSelectOnKeyDown,
    setFocus,
    Country,
    updateFieldValidationErrorsByFieldId,
    CountryComponent,
    handleCountryOnKeyDown,
    Organization,
    updateViewModelOrganization,
    handleEmailOnKeyDown,
    MarketConsentWrapper,
    submitForm,
    VerificationStepsEnum,
    isFormFilled,
    setRef,
    MedicalStatusComponent,
    MedicalProfessionalOrganizationComponent,
    FirstNameComponent,
    LastNameComponent,
    BirthDateComponent,
    PostalCodeComponent,
    EmailComponent,
    PhoneNumberComponent,
    FormFooterComponent,
} from '@sheerid/jslib';
import { produceDraftViewModel } from '../../lib/utils/stepComponentHelpers';
import { isRenderedInIframe } from '../IsRenderedInIframe';
import { LogoComponent } from '../LogoComponent/LogoComponent';

interface StepMedicalProfessionalPersonalInfoComponentProps {
    verificationService: VerificationService;
    intl: any;
}

export const StepMedicalProfessionalPersonalInfo = ({ intl, verificationService }: StepMedicalProfessionalPersonalInfoComponentProps) => {
    const viewModel = verificationService.viewModel as MedicalProfessionalPersonalInfoViewModel;
    const fieldValidationErrors: FieldValidationErrors & ExtendedFieldValidationErrors = verificationService.fieldValidationErrors;
    const verificationResponse = verificationService.verificationResponse as MedicalProfessionalPersonalInfoResponse;
    const failedInstantMatch = hasFailedInstantMatch(verificationResponse);
    const countryChoices = getAvailableCountryChoices(verificationService.programTheme, intl);
    const multipleCountriesConfigured = countryChoices && countryChoices.length > 1;
    const renderedInIframe = isRenderedInIframe();

    const updateMedicalProfessionalViewModel = (key: keyof MedicalProfessionalPersonalInfoViewModel, value: any) => {
        const nextState = produceDraftViewModel(viewModel, key, value);
        verificationService.updateViewModel(nextState);
    };

    const getAvailableMedicalStatuses = () => {
        const availableStatusesResponse = verificationResponse.availableStatuses;
        const availableStatuses: FormSelectChoice[] = [];

        for (const status of availableStatusesResponse) {
            availableStatuses.push({
                value: status,
                label: intl.formatMessage({ id: status, defaultMessage: MedicalProfessionalStatusDefaultMessagesEnum[status] }),
            });
        }
        return availableStatuses;
    };

    const handleMedicalStatusOnKeyDown: InputSelectOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            setFocus('organization');
        }
    };

    const updateCountryChoice = (countryChoice: FormSelectChoice<Country, string>) => {
        let nextState = produceDraftViewModel(viewModel, 'countryChoice', countryChoice);
        updateFieldValidationErrorsByFieldId('country', nextState.countryChoice, verificationService);
        if (countryChoice) {
            nextState = produceDraftViewModel(viewModel, 'country', countryChoice.value);
        } else {
            nextState = produceDraftViewModel(viewModel, 'country', undefined);
        }
        verificationService.updateViewModel(nextState);
    };

    React.useEffect(() => {
        if (!multipleCountriesConfigured) {
            verificationService.updateViewModel(produceDraftViewModel(viewModel, 'fieldsToSkipValidation', ['country']));
            return;
        }
        updateFieldValidationErrorsByFieldId('country', viewModel.countryChoice, verificationService);
    }, []);

    return (
        <div id="sid-step-active-medical-professional-personal-info" className={`${!renderedInIframe ? 'sid-l-container' : ''}`}>
            { failedInstantMatch ?
                <div className="sid-header">
                    <div className={`sid-logo sid-l-horz-center ${renderedInIframe ? 'sid-lightbox__logo' : ''}`}>
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className={`sid-header__title ${renderedInIframe ? 'sid-lightbox__title' : ''}`}>
                        <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Verify Your Details" />
                    </div>
                </div>
            :
                <div className="sid-header">
                    <div className={`sid-logo sid-l-horz-center ${renderedInIframe ? 'sid-lightbox__logo' : ''}`}>
                        <LogoComponent verificationService={verificationService} />
                    </div>
                    <div className={`sid-header__title ${renderedInIframe ? 'sid-lightbox__title' : ''}`}>
                        <FormattedHTMLMessage id="step.personalInfo.title" defaultMessage="Verify Your Details" />
                    </div>
                </div>
            }
            <div className={`sid-l-container ${renderedInIframe ? 'sid-lightbox' : ''}`}>
                <div className="sid-header__subtitle--bold">
                    <FormattedHTMLMessage id="step.personalInfo.subtitle"
                        defaultMessage="Complete the form below to confirm your eligibility." />
                    &nbsp;
                </div>
            {
                countryChoices.length > 1 ?
                    <CountryComponent
                        isRequired={true}
                        options={countryChoices}
                        value={viewModel.countryChoice}
                        isErrored={!!fieldValidationErrors.country}
                        onKeyDown={event => handleCountryOnKeyDown(event)}
                        onChange={updateCountryChoice}
                    />
                    : null
            }
            <MedicalStatusComponent
                value={{
                    value: viewModel.status,
                    label: MedicalProfessionalStatusDefaultMessagesEnum[viewModel.status],
                }}
                isErrored={!!fieldValidationErrors.status}
                options={ getAvailableMedicalStatuses() }
                onChange={
                    (status: FormSelectChoice) => {
                        updateMedicalProfessionalViewModel('metadata', { affiliationType: status ? (status.value as string) : '' });
                        updateMedicalProfessionalViewModel('status', status ? (status.value as string) : '');
                        updateFieldValidationErrorsByFieldId('status', status ? (status.value as string) : '', verificationService);
                        if (status) {
                            setFocus('organization');
                        }
                    }
                }
                onKeyDown={event => handleMedicalStatusOnKeyDown(event)}
            />

            <MedicalProfessionalOrganizationComponent
                isErrored={!!fieldValidationErrors.organization}
                onChange={
                    (choice: Organization) => {
                        updateViewModelOrganization(choice, verificationService);
                        updateFieldValidationErrorsByFieldId('organization', choice, verificationService);
                    }
                }
                value={(verificationService.viewModel as MedicalProfessionalPersonalInfoViewModel).organization}
                verificationService={verificationService}
            />

            <div className="sid-names">
                <FirstNameComponent

                    value={viewModel.firstName}
                    isErrored={!!fieldValidationErrors.firstName}
                    onChange={
                        (newValue: string) => {
                            updateMedicalProfessionalViewModel('firstName', newValue);
                            updateFieldValidationErrorsByFieldId('firstName', newValue, verificationService);
                        }
                    }
                />

                <LastNameComponent

                    value={viewModel.lastName}
                    isErrored={!!fieldValidationErrors.lastName}
                    onChange={
                        (newValue: string) => {
                            updateMedicalProfessionalViewModel('lastName', newValue);
                            updateFieldValidationErrorsByFieldId('lastName', newValue, verificationService);
                        }
                    }
                />
            </div>

            <BirthDateComponent
                isErrored={!!fieldValidationErrors.birthDate}
                errorId={fieldValidationErrors.birthDate}
                onChange={(newValue: string) => {
                    updateMedicalProfessionalViewModel('birthDate', newValue);
                    updateFieldValidationErrorsByFieldId('birthDate', newValue, verificationService);
                }}
                value={viewModel.birthDate}
            />

            <PostalCodeComponent
                isErrored={!!fieldValidationErrors.postalCode}
                onChange={(newValue: string) => {
                    updateMedicalProfessionalViewModel('postalCode', newValue);
                    updateFieldValidationErrorsByFieldId('postalCode', newValue, verificationService);
                }}
                value={viewModel.postalCode}
            />

            <EmailComponent
                value={viewModel.email}
                isErrored={!!fieldValidationErrors.email}
                onChange={
                    (newValue: string) => {
                        updateMedicalProfessionalViewModel('email', newValue);
                        updateFieldValidationErrorsByFieldId('email', newValue, verificationService);
                    }
                }
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleEmailOnKeyDown(event)}
            />
            {
                verificationService.programTheme.isSmsNotifierConfigured || verificationService.programTheme.smsLoopEnabled ?
                    <PhoneNumberComponent
                        isRequired={!!verificationService.programTheme.smsLoopEnabled}
                        value={viewModel.phoneNumber}
                        isErrored={!!fieldValidationErrors.phoneNumber}
                        onChange={
                            (newValue: string) => {
                                updateMedicalProfessionalViewModel('phoneNumber', newValue);
                                updateFieldValidationErrorsByFieldId('phoneNumber', newValue, verificationService);
                            }
                        }
                    />
                    : null
            }

            <MarketConsentWrapper
                verificationService={verificationService}
                isErrored={false}
                onChange={(newValue) => {
                    updateMedicalProfessionalViewModel('metadata', { ...verificationService.viewModel.metadata, marketConsentValue: newValue });
                    updateFieldValidationErrorsByFieldId('marketConsentValue', newValue, verificationService);
                }}
                viewModel={viewModel}
            />

            <div className="sid-form-region sid-submit-wrapper sid-l-space-top-md">
                <div className="sid-submit">
                    <button
                        id="sid-submit-btn-collect-info"
                        onClick={() => {
                            if (verificationService.viewModel.metadata.marketConsentValue) {
                                updateMedicalProfessionalViewModel('metadata', { 'opt-in': 1 });
                            }
                            submitForm(viewModel, verificationService, VerificationStepsEnum.collectMedicalProfessionalPersonalInfo, intl);
                        }}
                        type="submit"
                        className="sid-btn sid-btn--dark sid-l-full-width"
                        aria-label="submit"
                        ref={(button: HTMLButtonElement) => setRef('submitButton', button)}
                    >
                        { failedInstantMatch ?
                            <FormattedHTMLMessage id="tryAgain" defaultMessage="Try Again" />
                        :
                            <FormattedHTMLMessage id="verifyMyMedicalProfessionalStatus" defaultMessage="Verify My Medical Professional Status" />
                        }
                    </button>
                </div>
            </div>

            <FormFooterComponent verificationService={verificationService}>
                <FormattedHTMLMessage
                    id="MedicalProfessionalInfoShared"
                    defaultMessage={'Information entered here will be used for verification purposes.'}
                />
            </FormFooterComponent>
            </div>
        </div>
    );
};

export const StepMedicalProfessionalPersonalInfoComponent = injectIntl(StepMedicalProfessionalPersonalInfo);
